import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Folder, File as FileIcon, Save, RefreshCw, ChevronUp, Loader2, Code2 } from "lucide-react";

// Normalise un chemin (résout . et ..)
function joinPath(cwd, name) {
  const base = cwd.endsWith("/") ? cwd.slice(0, -1) : cwd;
  return `${base}/${name}`.replace(/\/+/g, "/");
}
function parentPath(cwd) {
  const clean = cwd.replace(/\/+$/, "");
  const idx = clean.lastIndexOf("/");
  return idx <= 0 ? "/" : clean.slice(0, idx);
}

export default function LabWebIDE({ sessionId }) {
  const { token } = useAuth();
  const [cwd, setCwd] = useState(".");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openFile, setOpenFile] = useState(null);
  const [content, setContent] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const listDir = useCallback(async (path) => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/labs/sessions/${sessionId}/fs`, { action: "list", path }, auth);
      setEntries(res.data.entries || []);
      setCwd(path);
    } catch {
      toast.error("Impossible de lister le dossier");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    listDir(".");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const openEntry = async (entry) => {
    const full = joinPath(cwd, entry.name);
    if (entry.is_dir) {
      listDir(full);
      return;
    }
    try {
      const res = await axios.post(`${API}/labs/sessions/${sessionId}/fs`, { action: "read", path: full }, auth);
      setOpenFile(full);
      setContent(res.data.content || "");
      setDirty(false);
    } catch {
      toast.error("Impossible d'ouvrir le fichier");
    }
  };

  const saveFile = async () => {
    if (!openFile) return;
    setSaving(true);
    try {
      await axios.post(`${API}/labs/sessions/${sessionId}/fs`, { action: "write", path: openFile, content }, auth);
      setDirty(false);
      toast.success("Fichier enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex bg-[#0d0d0d] rounded-lg overflow-hidden border border-[#222]">
      {/* Explorateur */}
      <div className="w-56 shrink-0 border-r border-[#222] flex flex-col min-h-0">
        <div className="flex items-center gap-1 px-2 h-9 border-b border-[#222] text-xs text-muted-foreground">
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => listDir(parentPath(cwd))} title="Dossier parent"><ChevronUp className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => listDir(cwd)} title="Rafraîchir"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <span className="truncate font-mono" title={cwd}>{cwd}</span>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : entries.length === 0 ? (
            <p className="text-xs text-muted-foreground px-3 py-2">Dossier vide</p>
          ) : (
            entries.map((e) => (
              <button key={e.name} onClick={() => openEntry(e)} className={`w-full flex items-center gap-2 px-3 py-1 text-sm text-left hover:bg-[#1a1a1a] ${openFile && openFile.endsWith("/" + e.name) ? "bg-[#1a1a1a]" : ""}`}>
                {e.is_dir ? <Folder className="w-4 h-4 text-primary shrink-0" /> : <FileIcon className="w-4 h-4 text-muted-foreground shrink-0" />}
                <span className="truncate">{e.name}</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Éditeur */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between px-3 h-9 border-b border-[#222]">
          <span className="text-sm font-mono truncate flex items-center gap-2">
            <Code2 className="w-4 h-4 text-primary" />
            {openFile || "Aucun fichier ouvert"}{dirty && <span className="text-yellow-400">●</span>}
          </span>
          {openFile && (
            <Button size="sm" onClick={saveFile} disabled={saving || !dirty} className="h-7">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</>}
            </Button>
          )}
        </div>
        {openFile ? (
          <textarea
            value={content}
            onChange={(e) => { setContent(e.target.value); setDirty(true); }}
            onKeyDown={(e) => { if (e.ctrlKey && e.key === "s") { e.preventDefault(); saveFile(); } }}
            spellCheck={false}
            className="flex-1 w-full bg-[#0a0a0a] text-green-100 font-mono text-[13px] p-3 outline-none resize-none leading-5"
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            Sélectionnez un fichier à gauche pour l'éditer.
          </div>
        )}
      </div>
    </div>
  );
}
